import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Generator.module.scss";
import { ToggleButton } from "./ToggleButton";
import { SiteswapGenerator } from "@jugglegym/core/lib/pattern/generator";

interface GeneratorProps {
  open: boolean;
  onSiteswapChange(ss: string): void;
}

interface GeneratorInput {
  propCount: number;
  maxHeight: number;
  sync: boolean;
  multiplex: boolean;
}

interface GeneratorOutput {
  generating: boolean;
  siteswaps: Array<string>;
}

export function Generator(props: GeneratorProps) {
  const [input, setInput] = useState({
    propCount: 3,
    maxHeight: 4,
    sync: false,
    multiplex: false,
    generating: false,
  } as GeneratorInput);
  const [output, setOutput] = useState({
    siteswaps: [],
    generating: false,
  } as GeneratorOutput);
  const genRef = useRef<SiteswapGenerator | null>(null);
  const queueRef = useRef<any>(0);

  function destroyQueue() {
    clearTimeout(queueRef.current);
  }

  function destroyGen() {
    if (genRef.current) {
      genRef.current.destroy();
      genRef.current = null;
    }
  }

  const runGen = useCallback(() => {
    destroyGen();
    setOutput({ generating: true, siteswaps: [] });
    genRef.current = new SiteswapGenerator({
      propCount: input.propCount,
      maxHeight: input.maxHeight,
      sync: input.sync,
      multiplex: input.multiplex,
      onDone: () => {
        setOutput((output) => {
          return {
            ...output,
            generating: false,
          };
        });
      },
      onSiteswapFound: (ss: string) => {
        setOutput((output) => {
          return { ...output, siteswaps: [...output.siteswaps, ss] };
        });
      },
    });
  }, [input]);

  const queueRunGen = useCallback(() => {
    destroyQueue();
    queueRef.current = setTimeout(() => runGen(), 10);
  }, [runGen]);

  useEffect(() => {
    if (props.open) {
      queueRunGen();
    }

    return () => {
      destroyQueue();
      destroyGen();
    };
  }, [queueRunGen, props.open]);

  return (
    <div className={styles.generator}>
      <div className={styles.filters}>
        <div className={styles.slider}>
          <ToggleButton active onClick={() => {}}>
            <span aria-label="Prop count" role="img">
              🔴
            </span>
            &times;{input.propCount}
          </ToggleButton>
          <input
            onChange={(e) => {
              const v = e.target.valueAsNumber;
              setInput((input) => {
                const input2 = { ...input, propCount: v };
                return input2;
              });
            }}
            value={input.propCount}
            type="range"
            min={1}
            max={9}
          />
        </div>
        <div className={styles.slider}>
          <ToggleButton active onClick={() => {}}>
            max {input.maxHeight}
          </ToggleButton>
          <input
            onChange={(e) => {
              const v = e.target.valueAsNumber;
              setInput((input) => {
                const input2 = { ...input, maxHeight: v };
                return input2;
              });
            }}
            value={input.maxHeight}
            type="range"
            min={1}
            max={9}
          />
        </div>
        <div>
          <ToggleButton
            active={input.multiplex}
            onClick={() => {
              setInput((input) => {
                const input2 = { ...input, multiplex: !input.multiplex };
                return input2;
              });
            }}
          >
            Multi
          </ToggleButton>
          <ToggleButton
            active={input.sync}
            onClick={() => {
              setInput((input) => {
                const input2 = { ...input, sync: !input.sync };
                return input2;
              });
            }}
          >
            Sync
          </ToggleButton>
        </div>
      </div>
      <div className={styles.patterns}>
        {output.siteswaps.length > 0 ? (
          output.siteswaps.map((ss: string, i) => {
            return (
              <div
                className={styles.pattern}
                onClick={(e) => props.onSiteswapChange(ss)}
                key={i}
              >
                {ss}
              </div>
            );
          })
        ) : (
          <div className={styles.none}>
            {output.generating ? "..." : "No siteswaps"}
          </div>
        )}
      </div>
    </div>
  );
}
